import { useState } from 'react';
import useFetch from './useFetch';
import {
  formatCapStructureForAPI,
  formatBasicInfoForAPI,
  formatFinancialsForAPI,
  formatHoldingsForAPI,
  formatCompsForAPI,
  validateHoldingsAgainstCapStructure,
  buildTransactionUpdatePayload
} from '../utils/companyDataFormatters';

export default function useCompanyDataUpload() {
  const [, companyUpdateRequest] = useFetch();
  const [, transactionUpdateRequest] = useFetch();
  const [, capStructureUpdateRequest] = useFetch();
  const [, compsEstimateRequest] = useFetch();
  const [, runCompsRequest] = useFetch();
  
  const [uploading, setUploading] = useState(false);
  
  /**
   * Upload company data with sequential API calls
   * @param {Object} parsedData - Parsed data from template
   * @param {Object} userData - Current user data context
   * @param {Function} onProgress - Callback for progress updates (step, progress)
   * @returns {Promise<Object>} - { success: boolean, results: {section: {success, error, data}} }
   */
  const uploadCompanyData = async (parsedData, userData, onProgress) => {
    setUploading(true);
    
    const results = {
      parse: { success: true, data: 'File parsed successfully' },
      basicInfo: { success: false },
      capStructure: { success: false, skipped: false },
      holdings: { success: false, skipped: false },
      financials: { success: false, skipped: false },
      comps: { success: false, skipped: false }
    };
    
    try {
      // Step 1: Parse & Validate (already done, 0-15%)
      onProgress(0, 15);
      
      // Step 2: Update Basic Info (15-25%)
      onProgress(1, 15);
      try {
        const basicInfo = formatBasicInfoForAPI(parsedData.basicInputs);
        
        // Update company data
        await new Promise((resolve, reject) => {
          companyUpdateRequest({
            url: '/companies/portfolio/update-company-data',
            method: 'post',
            body: {
              ...userData.companyData,
              capIqIdentifier: parsedData.basicInputs.subjectCompanyId || userData.companyData.capIqIdentifier
            },
            bodyIds: ['requestUserId'],
            onSuccess: () => resolve(),
            onError: (error) => reject(error)
          });
        });
        
        // Update transaction with basic info
        await new Promise((resolve, reject) => {
          transactionUpdateRequest({
            url: '/transactions/asc820/enterprise-update-transaction',
            method: 'post',
            body: {
              ...userData.transactionData,
              basicInfo: {
                ...userData.transactionData.basicInfo,
                ...basicInfo
              },
              version: parseInt(userData.transactionData.version, 10)
            },
            bodyIds: ['projectId', 'requestUserId'],
            onSuccess: (response) => {
              // Update userData reference for subsequent calls
              // eslint-disable-next-line no-param-reassign
              userData.transactionData = response;
              resolve(response);
            },
            onError: (error) => reject(error)
          });
        });
        
        results.basicInfo = { 
          success: true, 
          data: 'Basic information updated successfully' 
        };
        onProgress(1, 25);
      } catch (error) {
        results.basicInfo = { 
          success: false, 
          error: error.message || 'Failed to update basic information' 
        };
      }
      
      // Step 3: Update Cap Structure (25-50%)
      onProgress(2, 25);
      const hasCapStructure = parsedData.capStructure.preferred.length > 0 || 
                              parsedData.capStructure.common.length > 0;
      
      if (!hasCapStructure) {
        results.capStructure = { 
          success: true, 
          skipped: true, 
          data: 'No cap structure data in template' 
        };
        onProgress(2, 50);
      } else {
        try {
          const capStructureData = formatCapStructureForAPI(parsedData.capStructure, userData);
          capStructureData.capTableDate = parsedData.basicInputs.capTableDate;
          
          await new Promise((resolve, reject) => {
            capStructureUpdateRequest({
              url: '/calc-engine/update-cap-structure-object',
              method: 'post',
              body: {
                ...userData.capData,
                ...capStructureData
              },
              bodyIds: ['requestUserId'],
              onSuccess: (response) => {
                // eslint-disable-next-line no-param-reassign
                userData.capData = response;
                resolve(response);
              },
              onError: (error) => reject(error)
            });
          });
          
          const preferredCount = parsedData.capStructure.preferred.length;
          const commonCount = parsedData.capStructure.common.length;
          const warrantsCount = parsedData.capStructure.warrants.length;
          const optionsCount = parsedData.capStructure.options.length;
          
          results.capStructure = { 
            success: true, 
            data: `Updated: ${preferredCount} preferred, ${commonCount} common, ${warrantsCount} warrants, ${optionsCount} options` 
          };
          onProgress(2, 50);
        } catch (error) {
          results.capStructure = { 
            success: false, 
            error: error.message || 'Failed to update cap structure' 
          };
        }
      }
      
      // Step 4: Update Holdings (50-65%)
      onProgress(3, 50);
      const hasHoldings = parsedData.holdings.length > 0;
      
      if (!hasHoldings) {
        results.holdings = { 
          success: true, 
          skipped: true, 
          data: 'No holdings data in template' 
        };
        onProgress(3, 65);
      } else {
        try {
          // Validate holdings against cap structure (warning only, backend will validate)
          const validation = validateHoldingsAgainstCapStructure(
            parsedData.holdings,
            parsedData.capStructure
          );
          
          // Log warnings but don't fail - backend will handle validation
          if (!validation.isValid) {
            console.warn('Holdings validation warnings:', validation.errors);
          }
          
          const holdingsData = formatHoldingsForAPI(parsedData.holdings);
          
          await new Promise((resolve, reject) => {
            transactionUpdateRequest({
              url: '/transactions/asc820/enterprise-update-transaction',
              method: 'post',
              body: {
                ...userData.transactionData,
                holdings: holdingsData,
                version: parseInt(userData.transactionData.version, 10)
              },
              bodyIds: ['projectId', 'requestUserId'],
              onSuccess: (response) => {
                // eslint-disable-next-line no-param-reassign
                userData.transactionData = response;
                resolve(response);
              },
              onError: (error) => reject(error)
            });
          });
          
          results.holdings = { 
            success: true, 
            data: `${holdingsData.length} fund holdings added` 
          };
          onProgress(3, 65);
        } catch (error) {
          results.holdings = { 
            success: false, 
            error: error.message || 'Failed to update holdings' 
          };
        }
      }
      
      // Step 5: Update Financials (65-80%)
      onProgress(4, 65);
      const hasFinancials = Object.keys(parsedData.financials.balanceSheet).length > 0 ||
                            Object.keys(parsedData.financials.incomeStatement).length > 0;
      
      if (!hasFinancials) {
        results.financials = { 
          success: true, 
          skipped: true, 
          data: 'No financial data in template' 
        };
        onProgress(4, 80);
      } else {
        try {
          const basicInfo = formatBasicInfoForAPI(parsedData.basicInputs);
          const payload = buildTransactionUpdatePayload(
            userData,
            basicInfo,
            parsedData.financials,
            null
          );
          
          let resolved = false;
          await new Promise((resolve, reject) => {
            transactionUpdateRequest({
              url: '/transactions/asc820/enterprise-update-transaction',
              method: 'post',
              body: payload,
              bodyIds: ['projectId', 'requestUserId'],
              onSuccess: (response) => {
                if (!resolved) {
                  resolved = true;
                  // eslint-disable-next-line no-param-reassign
                  userData.transactionData = response;
                  resolve(response);
                }
              },
              onError: (error) => {
                if (!resolved) {
                  resolved = true;
                  reject(error);
                }
              },
              onFinally: () => {
                // Fallback: if neither onSuccess nor onError fired after 2 seconds, resolve anyway
                setTimeout(() => {
                  if (!resolved) {
                    resolved = true;
                    resolve(userData.transactionData);
                  }
                }, 2000);
              }
            });
          });
          
          results.financials = { 
            success: true, 
            data: 'Financial data updated successfully' 
          };
          onProgress(4, 80);
        } catch (error) {
          results.financials = { 
            success: false, 
            error: error.message || 'Failed to update financials' 
          };
          onProgress(4, 80);
        }
      }
      
      // Step 6: Run Comps (80-100%)
      onProgress(5, 80);
      const hasComps = parsedData.comps.ciqIds.length > 0;
      
      if (!hasComps) {
        results.comps = { 
          success: true, 
          skipped: true, 
          data: 'No comps data in template' 
        };
        onProgress(5, 100);
      } else {
        try {
          // First update transaction with industries (if any)
          if (userData.transactionData.basicInfo?.companyIndustry) {
            await new Promise((resolve, reject) => {
              transactionUpdateRequest({
                url: '/transactions/asc820/enterprise-update-transaction',
                method: 'post',
                body: {
                  ...userData.transactionData,
                  version: parseInt(userData.transactionData.version, 10)
                },
                bodyIds: ['requestUserId'],
                onSuccess: (response) => {
                  // eslint-disable-next-line no-param-reassign
                  userData.transactionData = response;
                  resolve(response);
                },
                onError: (error) => reject(error)
              });
            });
          }
          
          // Get existing comps data
          const existingComps = userData.compData?.comps || [];
          
          const compsData = formatCompsForAPI(
            parsedData.comps,
            parsedData.basicInputs,
            userData,
            existingComps
          );
          
          // Get estimate
          await new Promise((resolve, reject) => {
            compsEstimateRequest({
              url: '/comps/get-comps-estimate',
              method: 'post',
              body: {
                ...compsData,
                compData: existingComps
              },
              bodyIds: ['transactionId', 'requestUserId'],
              onSuccess: () => resolve(),
              onError: (error) => reject(error)
            });
          });
          
          onProgress(5, 90);
          
          // Run comps
          await new Promise((resolve, reject) => {
            runCompsRequest({
              url: '/comps/run-comps',
              method: 'post',
              body: compsData,
              bodyIds: ['transactionId', 'requestUserId'],
              customError: true,
              onSuccess: () => resolve(),
              onError: (error) => reject(error)
            });
          });
          
          results.comps = { 
            success: true, 
            data: `${parsedData.comps.ciqIds.length} companies added, analysis running` 
          };
          onProgress(5, 100);
        } catch (error) {
          results.comps = { 
            success: false, 
            error: error.message || 'Failed to run comps' 
          };
          onProgress(5, 100);
        }
      }
      
      // Determine overall success
      const overallSuccess = results.basicInfo.success &&
                            (results.capStructure.success || results.capStructure.skipped) &&
                            (results.holdings.success || results.holdings.skipped) &&
                            (results.financials.success || results.financials.skipped) &&
                            (results.comps.success || results.comps.skipped);
      
      setUploading(false);
      return { success: overallSuccess, results };
      
    } catch (error) {
      setUploading(false);
      return { 
        success: false, 
        results,
        error: error.message 
      };
    }
  };
  
  return {
    uploadCompanyData,
    uploading
  };
}

